import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { getTeamLogoUrl } from '../utils/logoUrl';
import { formatDate } from '../utils/dateUtils';
import { invalidateUpcomingMatchesCache } from '../utils/cacheUtils';
import ScoreInput from '../components/ScoreInput';
import EmptyStateCard from '../components/EmptyStateCard';
import BankerButton from '../components/BankerButton';
import { getBankerMatchIdInWeek } from '../utils/bankerUtils';
import ScoredMatchSummary, { MissedMatchSummary, PendingPredictionSummary } from '../components/ScoredMatchSummary';
import { getPredictionOutcome, getOutcomeCardClasses, hasMatchResult, isScoredOutcome, getOutcomeAccentColor, getOutcomeMutedTextColor } from '../utils/predictionOutcomeUtils';
import { usePredictionAutoSubmit } from '../hooks/usePredictionAutoSubmit';
import TeamFormIndicator from '../components/TeamFormIndicator';
import { useTeamForms } from '../hooks/useTeamForms';

interface MatchGroup {
  id: string;
  name: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  maxBankers?: number;
  matchWeek: number;
}

interface Team {
  id: string;
  name: string;
  shortName?: string;
  logoUrl?: string;
}

interface Match {
  id: string;
  season: { id: string; name: string };
  matchGroup: { id: string; name: string };
  homeTeam: Team;
  awayTeam: Team;
  matchDate: string;
  venue: string;
  isCup: boolean;
  predictionsClose: string;
  status: string;
  homeGoals?: number;
  awayGoals?: number;
}

interface Prediction {
  id: string;
  match: {
    id: string;
    homeTeam: {
      name: string;
      shortName?: string;
      logoUrl?: string;
    };
    awayTeam: {
      name: string;
      shortName?: string;
      logoUrl?: string;
    };
    matchDate: string;
    predictionsClose: string;
    homeGoals?: number;
    awayGoals?: number;
    status: string;
  };
  homeGoals: number;
  awayGoals: number;
  isBanker: boolean;
  points?: number;
  exactScorePoints?: number;
  correctResultPoints?: number;
  bankerBonusPoints?: number;
  isScored: boolean;
  submittedAt: string;
}

interface Season {
  id: string;
  name: string;
  gameType: string;
  countryCode: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  rules: {
    pointsForExactScore: number;
    pointsForCorrectResult: number;
    pointsForBanker: number;
    maxBankersPerRound: number;
  };
}

const SeasonDetail: React.FC = () => {
  const { id: seasonId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [season, setSeason] = useState<Season | null>(null);
  const [matchGroups, setMatchGroups] = useState<MatchGroup[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMatchGroup, setSelectedMatchGroup] = useState<string>('');
  const [currentMatchGroup, setCurrentMatchGroup] = useState<MatchGroup | null>(null);
  const [predictionForms, setPredictionForms] = useState<Record<string, { homeGoals: string; awayGoals: string; isBanker: boolean }>>({});
  const [submitting, setSubmitting] = useState<Record<string, boolean>>({});
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [isGeneratingLuckyPicks, setIsGeneratingLuckyPicks] = useState(false);
  const [showPreviousMatchweeks, setShowPreviousMatchweeks] = useState(false);

  useEffect(() => {
    const fetchSeasonData = async () => {
      try {
        setLoading(true);
        
        // Fetch season details
        const seasonResponse = await apiClient.get(`/seasons/${seasonId}`);
        setSeason(seasonResponse.data);
        
        // Fetch match groups
        const groupsResponse = await apiClient.get(`/seasons/${seasonId}/match-groups`);
        setMatchGroups(groupsResponse.data);
        
        // Fetch matches
        const matchesResponse = await apiClient.get(`/matches?season=${seasonId}`);
        setMatches(matchesResponse.data);
        
        // Determine the current active match group based on dates
        const now = new Date();
        const activeGroup = determineActiveMatchGroup(groupsResponse.data, matchesResponse.data, now);
        
        if (activeGroup) {
          setCurrentMatchGroup(activeGroup);
        } else {
          // Fallback: try to get current match group from API
          try {
            const currentGroupResponse = await apiClient.get(`/seasons/${seasonId}/current-match-group`);
            setCurrentMatchGroup(currentGroupResponse.data);
          } catch (err) {
            // If no current match group, use the first one (season hasn't started yet)
            if (groupsResponse.data.length > 0) {
              setCurrentMatchGroup(groupsResponse.data[0]);
            }
          }
        }
        
        // Fetch user's predictions for this season
        if (user) {
          try {
            const predictionsResponse = await apiClient.get(`/predictions?season=${seasonId}`);
            setPredictions(predictionsResponse.data);
            
            // Initialize predictionForms using the fresh predictions data
            const initialForms: Record<string, { homeGoals: string; awayGoals: string; isBanker: boolean }> = {};
            matchesResponse.data.forEach((match: Match) => {
              const prediction = predictionsResponse.data.find((p: Prediction) => p.match.id === match.id);
              
              if (prediction) {
                initialForms[match.id] = {
                  homeGoals: prediction.homeGoals.toString(),
                  awayGoals: prediction.awayGoals.toString(),
                  isBanker: prediction.isBanker,
                };
              } else {
                initialForms[match.id] = {
                  homeGoals: '',
                  awayGoals: '',
                  isBanker: false,
                };
              }
            });
            setPredictionForms(initialForms);
          } catch (err) {
            // If predictions fetch fails, just continue without predictions
            const initialForms: Record<string, { homeGoals: string; awayGoals: string; isBanker: boolean }> = {};
            matchesResponse.data.forEach((match: Match) => {
              initialForms[match.id] = {
                homeGoals: '',
                awayGoals: '',
                isBanker: false,
              };
            });
            setPredictionForms(initialForms);
          }
        } else {
          // Initialize empty forms for non-logged-in users
          const initialForms: Record<string, { homeGoals: string; awayGoals: string; isBanker: boolean }> = {};
          matchesResponse.data.forEach((match: Match) => {
            initialForms[match.id] = {
              homeGoals: '',
              awayGoals: '',
              isBanker: false,
            };
          });
          setPredictionForms(initialForms);
        }
        
      } catch (err: any) {
        console.error('Error loading season data:', err);
        setError(err.response?.data?.message || 'Failed to load season data');
      } finally {
        setLoading(false);
      }
    };

    if (seasonId) {
      fetchSeasonData();
    }
  }, [seasonId, user]);

  // Check scroll position to show/hide arrows
  const checkScrollPosition = () => {
    const container = document.getElementById('matchgroup-tabs');
    if (container) {
      setShowLeftArrow(container.scrollLeft > 0);
      setShowRightArrow(container.scrollLeft < container.scrollWidth - container.clientWidth);
    }
  };

  // Helper function to scroll to active tab with multiple attempts (for client-side navigation)
  const scrollToActiveTabWithRetries = () => {
    const attemptScroll = () => {
      const container = document.getElementById('matchgroup-tabs');
      const activeButton = container?.querySelector('.btn-primary') as HTMLElement;
      
      if (container && activeButton) {
        // Always center the active tab for better visibility
        const scrollLeft = activeButton.offsetLeft - container.clientWidth / 2 + activeButton.clientWidth / 2;
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
        
        // Update arrow visibility after scroll
        setTimeout(checkScrollPosition, 300);
        return true;
      }
      return false;
    };

    // Try multiple times to handle client-side navigation timing issues
    attemptScroll();
    setTimeout(attemptScroll, 100);
    setTimeout(attemptScroll, 300);
    setTimeout(attemptScroll, 500);
  };

  // Update selectedMatchGroup when currentMatchGroup changes
  useEffect(() => {
    if (currentMatchGroup) {
      setSelectedMatchGroup(currentMatchGroup.id);
      
      // Center the current match group tab when it's set
      setTimeout(() => {
        scrollToActiveTabWithRetries();
      }, 200);
    }
  }, [currentMatchGroup]);

  // Handle matchweek query parameter to set appropriate match week
  useEffect(() => {
    const matchweekParam = searchParams.get('matchweek');
    
    if (matchweekParam && matchGroups.length > 0 && matches.length > 0) {
      const now = new Date();
      let targetGroupId: string | null = null;
      
      if (matchweekParam === 'next') {
        // Find the next upcoming match week
        const upcomingGroups = matchGroups
          .filter(group => {
            const groupMatches = matches.filter(match => match.matchGroup.id === group.id);
            if (groupMatches.length === 0) return false;
            
            const earliestMatch = groupMatches.reduce((earliest, match) => {
              const matchDate = new Date(match.matchDate);
              return matchDate < earliest ? matchDate : earliest;
            }, new Date(groupMatches[0].matchDate));
            
            const daysDiff = (earliestMatch.getTime() - now.getTime()) / (1000 * 60 * 60 * 36);
            return daysDiff > 1; // More than 36 hours in the future
          })
          .sort((a, b) => {
            const aMatches = matches.filter(match => match.matchGroup.id === a.id);
            const bMatches = matches.filter(match => match.matchGroup.id === b.id);
            
            const aEarliest = aMatches.reduce((earliest, match) => {
              const matchDate = new Date(match.matchDate);
              return matchDate < earliest ? matchDate : earliest;
            }, new Date(aMatches[0].matchDate));
            
            const bEarliest = bMatches.reduce((earliest, match) => {
              const matchDate = new Date(match.matchDate);
              return matchDate < earliest ? matchDate : earliest;
            }, new Date(bMatches[0].matchDate));
            
            return aEarliest.getTime() - bEarliest.getTime();
          });
        
        if (upcomingGroups.length > 0) {
          targetGroupId = upcomingGroups[0].id;
        }
      } else if (matchweekParam === 'current') {
        // Use the current match group logic (already handled by determineActiveMatchGroup)
        if (currentMatchGroup) {
          targetGroupId = currentMatchGroup.id;
        }
      } else if (matchweekParam.startsWith('matchweek-')) {
        // Handle specific matchweek number (e.g., matchweek-4)
        const matchweekNumber = parseInt(matchweekParam.replace('matchweek-', ''));
        if (!isNaN(matchweekNumber)) {
          // Find the match group with this matchweek number
          const targetGroup = matchGroups.find(group => group.matchWeek === matchweekNumber);
          if (targetGroup) {
            targetGroupId = targetGroup.id;
          }
        }
      } else {
        // Handle direct match group ID (from MyPredictions link)
        const targetGroup = matchGroups.find(group => group.id === matchweekParam);
        if (targetGroup) {
          targetGroupId = targetGroup.id;
        }
      }
      
      if (targetGroupId) {
        setSelectedMatchGroup(targetGroupId);
        
        // Auto-expand "Previous Matchweeks" if selecting an old matchweek
        const targetGroup = matchGroups.find(g => g.id === targetGroupId);
        if (targetGroup && currentMatchGroup && targetGroup.matchWeek < currentMatchGroup.matchWeek - 1) {
          setShowPreviousMatchweeks(true);
        }
        
        // Explicitly trigger scroll after setting the match group (for client-side navigation)
        // The selectedMatchGroup effect will also handle this, but this ensures it works
        setTimeout(() => {
          scrollToActiveTabWithRetries();
        }, 100);
      }
    }
  }, [searchParams, matchGroups, matches, currentMatchGroup]);

  // Function to determine the active match group based on current date
  const determineActiveMatchGroup = (groups: MatchGroup[], matches: Match[], currentDate: Date): MatchGroup | null => {
    // Create a map of match groups to their matches
    const groupMatches = new Map<string, Match[]>();
    
    matches.forEach(match => {
      const groupId = match.matchGroup.id;
      if (!groupMatches.has(groupId)) {
        groupMatches.set(groupId, []);
      }
      groupMatches.get(groupId)!.push(match);
    });
    
    // Sort groups by their earliest match date
    const sortedGroups = groups
      .filter(group => {
        const groupMatchList = groupMatches.get(group.id);
        return groupMatchList && groupMatchList.length > 0;
      })
      .sort((a, b) => {
        const aMatches = groupMatches.get(a.id) || [];
        const bMatches = groupMatches.get(b.id) || [];
        if (aMatches.length === 0 || bMatches.length === 0) return 0;
        
        const aEarliest = new Date(aMatches.sort((x, y) => new Date(x.matchDate).getTime() - new Date(y.matchDate).getTime())[0].matchDate);
        const bEarliest = new Date(bMatches.sort((x, y) => new Date(x.matchDate).getTime() - new Date(y.matchDate).getTime())[0].matchDate);
        
        return aEarliest.getTime() - bEarliest.getTime();
      });
    
    // Find the current group: latest group that became active, until next group becomes active
    for (let i = 0; i < sortedGroups.length; i++) {
      const group = sortedGroups[i];
      const nextGroup = sortedGroups[i + 1];
      
      const groupMatchList = groupMatches.get(group.id);
      if (!groupMatchList || groupMatchList.length === 0) continue;
      
      // Sort matches by date to find the earliest match in this group
      const sortedMatches = groupMatchList.sort((a, b) => new Date(a.matchDate).getTime() - new Date(b.matchDate).getTime());
      const earliestMatchDate = new Date(sortedMatches[0].matchDate);
      const latestMatchDate = new Date(sortedMatches[sortedMatches.length - 1].matchDate);
      
      // Check if this group has become active (36 hours before first match)
      const daysDiffEarliest = (earliestMatchDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 36);
      
      if (daysDiffEarliest <= 1) {
        // Check if next group has become active yet
        if (nextGroup) {
          const nextGroupMatches = groupMatches.get(nextGroup.id);
          if (nextGroupMatches && nextGroupMatches.length > 0) {
            const nextGroupEarliest = new Date(nextGroupMatches.sort((x, y) => new Date(x.matchDate).getTime() - new Date(y.matchDate).getTime())[0].matchDate);
            const nextGroupDaysDiff = (nextGroupEarliest.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 36);
            
            if (nextGroupDaysDiff > 1) {
              // Next group hasn't become active yet, so this group is still current
              return group;
            }
          }
        } else {
          // This is the last/latest group, so it's current if it became active
          return group;
        }
      }
    }
    
    // If no current group found, return the next upcoming group
    const upcomingGroups = sortedGroups.filter(group => {
      const groupMatchList = groupMatches.get(group.id);
      if (!groupMatchList || groupMatchList.length === 0) return false;
      
      const sortedMatches = groupMatchList.sort((a, b) => new Date(a.matchDate).getTime() - new Date(b.matchDate).getTime());
      const earliestMatchDate = new Date(sortedMatches[0].matchDate);
      const daysDiff = (earliestMatchDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 36);
      
      return daysDiff > 1;
    });
    
    if (upcomingGroups.length > 0) {
      return upcomingGroups[0];
    }
    
    return null;
  };

  // Re-initialize prediction forms when matches or predictions change (backup safety net)
  useEffect(() => {
    if (matches.length > 0 && user) {
      const hasEmptyForms = matches.some(match => {
        const form = predictionForms[match.id];
        const prediction = predictions.find(p => p.match.id === match.id);
        return prediction && (!form || (form.homeGoals === '' && form.awayGoals === ''));
      });

      if (hasEmptyForms) {
        const updatedForms: Record<string, { homeGoals: string; awayGoals: string; isBanker: boolean }> = {};
        matches.forEach((match: Match) => {
          const prediction = predictions.find((p: Prediction) => p.match.id === match.id);
          if (prediction) {
            updatedForms[match.id] = {
              homeGoals: prediction.homeGoals.toString(),
              awayGoals: prediction.awayGoals.toString(),
              isBanker: prediction.isBanker,
            };
          } else {
            updatedForms[match.id] = predictionForms[match.id] || {
              homeGoals: '',
              awayGoals: '',
              isBanker: false,
            };
          }
        });
        setPredictionForms(updatedForms);
      }
    }
  }, [matches, predictions, user]);

  // Categorize matchgroups for collapsible UI
  const categorizedMatchGroups = useMemo(() => {
    if (!currentMatchGroup || matchGroups.length === 0) {
      return {
        current: null,
        previous: null,
        old: matchGroups.sort((a, b) => a.matchWeek - b.matchWeek),
        future: []
      };
    }

    const currentWeek = currentMatchGroup.matchWeek;
    const sortedGroups = [...matchGroups].sort((a, b) => a.matchWeek - b.matchWeek);
    
    const current = currentMatchGroup;
    const previous = sortedGroups.find(g => g.matchWeek === currentWeek - 1) || null;
    const old = sortedGroups.filter(g => g.matchWeek < currentWeek - 1);
    const future = sortedGroups.filter(g => g.matchWeek > currentWeek);

    return { current, previous, old, future };
  }, [matchGroups, currentMatchGroup]);

  // Auto-scroll to center active tab when selection changes
  useEffect(() => {
    scrollToActiveTabWithRetries();
    
    // Auto-expand "Previous Matchweeks" if an old matchweek is selected
    if (categorizedMatchGroups.old.some(g => g.id === selectedMatchGroup)) {
      setShowPreviousMatchweeks(true);
    }
  }, [selectedMatchGroup, categorizedMatchGroups.old]);

  // Initial arrow check and centering when match groups load
  useEffect(() => {
    if (matchGroups.length > 0) {
      setTimeout(() => {
        checkScrollPosition();
        scrollToActiveTabWithRetries();
      }, 100);
    }
  }, [matchGroups]);

  const getPredictionForMatch = (matchId: string): Prediction | undefined => {
    const prediction = predictions.find(p => p.match.id === matchId);
    return prediction;
  };

  const isPredictionOpen = (match: Match): boolean => {
    return new Date() < new Date(match.predictionsClose);
  };

  const filteredMatches = useMemo(() => {
    if (!selectedMatchGroup) {
      return matches; // Show all matches if no selection yet
    }
    return matches.filter(match => match.matchGroup.id === selectedMatchGroup);
  }, [matches, selectedMatchGroup]);

  const bankerMatchIdInWeek = useMemo(
    () => getBankerMatchIdInWeek(filteredMatches.map(match => match.id), predictions, predictionForms),
    [filteredMatches, predictions, predictionForms]
  );

  const openMatchIds = useMemo(
    () => filteredMatches.filter(match => isPredictionOpen(match)).map(match => match.id),
    [filteredMatches]
  );

  const cancelAutoSubmitRef = useRef<(matchId: string) => void>(() => {});

  // Memoize team IDs from filtered matches to avoid unnecessary API calls
  const teamIdsFromMatches = useMemo(() => {
    const teamIds = new Set<string>();
    filteredMatches.forEach(match => {
      teamIds.add(match.homeTeam.id);
      teamIds.add(match.awayTeam.id);
    });
    return Array.from(teamIds).sort(); // Sort for stable comparison
  }, [filteredMatches]);

  const teamForms = useTeamForms(teamIdsFromMatches, Boolean(user));

  const handlePredictionSubmit = useCallback(async (matchId: string) => {
    const form = predictionForms[matchId];
    if (!form || !form.homeGoals || !form.awayGoals) {
      return;
    }

    // Validate that the scores are valid numbers
    const homeGoals = parseInt(form.homeGoals);
    const awayGoals = parseInt(form.awayGoals);
    
    if (isNaN(homeGoals) || isNaN(awayGoals) || homeGoals < 0 || awayGoals < 0) {
      alert('Please enter valid scores (0 or greater)');
      return;
    }

    setSubmitting(prev => ({ ...prev, [matchId]: true }));
    
    try {
      const predictionData = {
        match: matchId,
        homeGoals: homeGoals,
        awayGoals: awayGoals,
        isBanker: form.isBanker
      };

      const existingPrediction = predictions.find(p => p.match.id === matchId);
      
      if (existingPrediction) {
        // Update existing prediction
        await apiClient.put(`/predictions/${existingPrediction.id}`, predictionData);
      } else {
        // Create new prediction
        await apiClient.post('/predictions', predictionData);
      }

      // Refresh predictions
      const predictionsResponse = await apiClient.get(`/predictions?season=${seasonId}`);
      setPredictions(predictionsResponse.data);
      
      // Update the form with the new prediction data
      const updatedPrediction = predictionsResponse.data.find((p: Prediction) => p.match.id === matchId);
      if (updatedPrediction) {
        setPredictionForms(prev => ({
          ...prev,
          [matchId]: {
            homeGoals: updatedPrediction.homeGoals.toString(),
            awayGoals: updatedPrediction.awayGoals.toString(),
            isBanker: updatedPrediction.isBanker
          }
        }));
      }
      
      // Invalidate upcoming matches cache to refresh homepage
      invalidateUpcomingMatchesCache();
      
      cancelAutoSubmitRef.current(matchId);
      
    } catch (error: any) {
      console.error('Error submitting prediction:', error);
      alert(error.response?.data?.message || 'Failed to submit prediction');
    } finally {
      setSubmitting(prev => ({ ...prev, [matchId]: false }));
    }
  }, [predictionForms, predictions, seasonId]);

  const { countdownValues, cancelAutoSubmit, hasActiveCountdown } = usePredictionAutoSubmit({
    predictionForms,
    predictions,
    submitting,
    onSubmit: handlePredictionSubmit,
    eligibleMatchIds: openMatchIds,
  });

  cancelAutoSubmitRef.current = cancelAutoSubmit;

  const handlePredictionChange = (matchId: string, field: 'homeGoals' | 'awayGoals' | 'isBanker', value: string | boolean) => {
    setPredictionForms(prev => {
      const existingForm = prev[matchId] || { homeGoals: '', awayGoals: '', isBanker: false };
      const newForm = {
        ...existingForm,
        [field]: value
      };
      
      return {
        ...prev,
        [matchId]: newForm
      };
    });
  };

  const handleBankerToggle = (matchId: string) => {
    const matchIdsInWeek = filteredMatches.map(match => match.id);
    setPredictionForms(prev => {
      const form = prev[matchId];
      const saved = predictions.find(p => p.match.id === matchId);
      const current = form !== undefined ? Boolean(form.isBanker) : Boolean(saved?.isBanker);
      const newValue = !current;
      const updated = { ...prev };

      if (newValue) {
        matchIdsInWeek.forEach(id => {
          if (id === matchId) {
            updated[id] = {
              ...(updated[id] || { homeGoals: '', awayGoals: '', isBanker: false }),
              isBanker: true,
            };
          } else if (updated[id]) {
            updated[id] = { ...updated[id], isBanker: false };
          }
        });
      } else {
        updated[matchId] = {
          ...(updated[matchId] || { homeGoals: '', awayGoals: '', isBanker: false }),
          isBanker: false,
        };
      }

      return updated;
    });
  };

  // Helper function to check if both scores are filled
  const areBothScoresFilled = (matchId: string): boolean => {
    const form = predictionForms[matchId];
    return form && form.homeGoals !== '' && form.awayGoals !== '';
  };

  const generateLuckyPicks = () => {
    if (!user || !seasonId) return;
    
    setIsGeneratingLuckyPicks(true);
    
    // Get all open matches for the current match group
    const openMatches = filteredMatches.filter(match => isPredictionOpen(match));
    
    if (openMatches.length === 0) {
      alert('No open matches to generate predictions for!');
      setIsGeneratingLuckyPicks(false);
      return;
    }
    
    // Generate random predictions for each open match
    const newForms = { ...predictionForms };
    
    openMatches.forEach(match => {
      // Generate random scores (0-4 is a reasonable range)
      const homeGoals = Math.floor(Math.random() * 5);
      const awayGoals = Math.floor(Math.random() * 5);
      
      // Never set as banker for lucky picks
      const isBanker = false;
      
      newForms[match.id] = {
        homeGoals: homeGoals.toString(),
        awayGoals: awayGoals.toString(),
        isBanker: isBanker
      };
    });
    
    setPredictionForms(newForms);
    setIsGeneratingLuckyPicks(false);
    
    // Show a fun message
    alert(`🎲 Lucky picks generated! ${openMatches.length} random predictions created. Review and submit them!`);
  };



  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading season data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600 text-xl">{error}</div>
      </div>
    );
  }

  if (!season) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Season not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden">
      <div className="max-w-7xl mx-auto py-6 px-2 sm:px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/seasons" className="mb-4 inline-block" style={{ color: 'var(--highlight)' }}>
            ← Back to Competitions &amp; Fixtures
          </Link>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>{season.name}</h1>
{/*           <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            {formatDate(season.startDate)} - {formatDate(season.endDate)}
          </p> */}
        </div>


                {/* Lucky Picks Button */}
                {user && filteredMatches.length > 0 && (
          <div className="mb-6 text-right max-sm:text-left overflow-x-hidden">
            <button
              onClick={generateLuckyPicks}
              disabled={isGeneratingLuckyPicks}
              className="inline-flex items-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              style={{
                backgroundColor: 'var(--highlight)',
                color: 'black'
              }}
            >
              {isGeneratingLuckyPicks ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating Lucky Picks...
                </>
              ) : (
                <>
                  🎲 I'm feeling lucky, pick for me!
                </>
              )}
            </button>
          </div>
        )}


        {/* Knockout Stage Info */}
{/*         <div className="card rounded-lg shadow p-4 md:p-5 mb-6 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                Penalty shootouts are not included in prediction results. Matches that go to penalties are considered draws for prediction purposes, based on the final score before penalties.
              </p>
            </div>
          </div>
        </div> */}


        {/* Match Group Filter */}
        <div className="mb-6">
          <div className="relative">
            {/* Left scroll button */}
            {showLeftArrow && (
              <button
                onClick={() => {
                  const container = document.getElementById('matchgroup-tabs');
                  if (container) {
                    container.scrollBy({ left: -200, behavior: 'smooth' });
                    setTimeout(checkScrollPosition, 300);
                  }
                }}
                className="absolute left-0 top-0 bottom-0 z-10 w-8 bg-gradient-to-r from-[#eff5f6] to-transparent dark:from-gray-900 dark:to-transparent flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
                style={{ marginLeft: '-8px' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Scrollable tabs container */}
            <div 
              id="matchgroup-tabs"
              className="flex gap-2 overflow-x-auto scrollbar-hide pr-4 sm:pr-8"
              onScroll={checkScrollPosition}
            >
              {/* Previous Matchweeks (collapsed) - shown first */}
              {categorizedMatchGroups.old.length > 0 && (
                <>
                  <button
                    onClick={() => setShowPreviousMatchweeks(!showPreviousMatchweeks)}
                    className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap flex-shrink-0 flex items-center gap-1 ${
                      showPreviousMatchweeks
                        ? 'btn-primary'
                        : 'btn-secondary'
                    }`}
                  >
                    Previous Weeks
                    <svg 
                      className={`w-4 h-4 transition-transform ${showPreviousMatchweeks ? '-rotate-90' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Expanded old matchweeks */}
                  {showPreviousMatchweeks && categorizedMatchGroups.old.map(group => (
                    <button
                      key={group.id}
                      onClick={() => setSelectedMatchGroup(group.id)}
                      className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                        selectedMatchGroup === group.id
                          ? 'btn-primary'
                          : 'btn-secondary'
                      }`}
                    >
                      {group.name}
                    </button>
                  ))}
                </>
              )}
              
              {/* Previous matchweek (1 week back) */}
              {categorizedMatchGroups.previous && (
                <button
                  onClick={() => setSelectedMatchGroup(categorizedMatchGroups.previous!.id)}
                  className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                    selectedMatchGroup === categorizedMatchGroups.previous.id
                      ? 'btn-primary'
                      : 'btn-secondary'
                  }`}
                >
                  {categorizedMatchGroups.previous.name}
                </button>
              )}
              
              {/* Current matchweek */}
              {categorizedMatchGroups.current && (
                <button
                  onClick={() => setSelectedMatchGroup(categorizedMatchGroups.current!.id)}
                  className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                    selectedMatchGroup === categorizedMatchGroups.current.id
                      ? 'btn-primary'
                      : 'btn-secondary'
                  }`}
                >
                  {categorizedMatchGroups.current.name}
                </button>
              )}
              
              {/* Future matchweeks (all shown) */}
              {categorizedMatchGroups.future.map(group => (
                <button
                  key={group.id}
                  onClick={() => setSelectedMatchGroup(group.id)}
                  className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                    selectedMatchGroup === group.id
                      ? 'btn-primary'
                      : 'btn-secondary'
                  }`}
                >
                  {group.name}
                </button>
              ))}
            </div>

            {/* Right scroll button */}
            {showRightArrow && (
              <button
                onClick={() => {
                  const container = document.getElementById('matchgroup-tabs');
                  if (container) {
                    container.scrollBy({ left: 200, behavior: 'smooth' });
                    setTimeout(checkScrollPosition, 300);
                  }
                }}
                className="absolute right-0 top-0 bottom-0 z-10 w-8 bg-gradient-to-l from-[#eff5f6] to-transparent dark:from-gray-900 dark:to-transparent flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
                style={{ marginRight: '-8px' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        </div>



        {/* Matches */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
          {filteredMatches.length === 0 ? (
            <div className="col-span-full">
              <EmptyStateCard
                title="No matches available yet"
                message="There are no fixtures scheduled for this matchweek yet. Try selecting a different week, or check back soon."
              />
            </div>
          ) : (
            filteredMatches.map(match => {
              const prediction = getPredictionForMatch(match.id);
              const predictionOpen = isPredictionOpen(match);
              const outcome = getPredictionOutcome(
                match.homeGoals,
                match.awayGoals,
                prediction,
                predictionOpen
              );
              const scored = isScoredOutcome(outcome);
              const accentClass = scored ? getOutcomeAccentColor(outcome) : '';
              const mutedClass = scored ? getOutcomeMutedTextColor(outcome) : '';
              
              return (
                <div key={match.id} className={`card rounded-lg p-3 sm:p-4 md:p-6 ${scored ? 'max-md:pb-6 md:min-h-[465px]' : 'min-h-[465px]'} relative ${getOutcomeCardClasses(outcome, Boolean(prediction))}`}>
                  <div className="flex items-center justify-between mb-4 overflow-x-hidden">
                    {/* Prediction Submitted Status Pill */}
                    {prediction && !scored && (
                      <div className="absolute top-3 sm:top-5 right-2 sm:right-3">
                        <span className="px-2 py-1 text-xs rounded-full font-medium whitespace-nowrap bg-highlight text-black">
                          {predictionOpen ? '⚽️ Predicted' : 'Locked in'}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center space-x-4">
                      <div
                        className={`text-sm ${mutedClass}`}
                        style={!scored ? { color: 'var(--text-secondary)' } : undefined}
                      >
                        {formatDate(match.matchDate)}
                      </div>
{/*                       <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {match.venue}
                      </div> */}
                      {match.isCup && (
                        <span className="px-2 py-1 text-xs rounded" style={{ 
                          backgroundColor: 'rgba(234, 179, 8, 0.2)',
                          color: '#eab308'
                        }}>
                          Cup
                        </span>
                      )}
                    </div>
                    
                  </div>

                  <div className="text-center pt-4 mb-4 overflow-x-hidden">
                    <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 items-center">
                      <div className="text-center">
                        {match.homeTeam.logoUrl && (
                          <img 
                            src={getTeamLogoUrl(match.homeTeam.logoUrl)}
                            alt={`${match.homeTeam.name} logo`}
                            className="w-8 h-8 sm:w-10 sm:h-10 object-contain justify-center mx-auto mb-2"
                          />
                        )}
                        <div className={`font-semibold text-sm sm:text-base break-words ${accentClass}`} style={!scored ? { color: 'var(--text-primary)' } : undefined}>{match.homeTeam.name}</div>
                        {user && <TeamFormIndicator teamId={match.homeTeam.id} teamForms={teamForms} />}
                      </div>
                      
                      <div className="text-center">
                        {hasMatchResult(match.homeGoals, match.awayGoals) ? (
                          <div>
                            <div className={`text-2xl sm:text-3xl font-bold tabular-nums ${accentClass}`} style={!scored ? { color: 'var(--text-primary)' } : undefined}>
                              {match.homeGoals} - {match.awayGoals}
                            </div>
                            <div
                              className={`text-xs sm:text-sm font-bold uppercase tracking-wider mt-1 ${scored ? mutedClass : ''}`}
                              style={!scored ? { color: '#22c55e' } : undefined}
                            >
                              {scored ? 'Full time' : 'Final result'}
                            </div>
                          </div>
                        ) : (
                          <div className="text-lg sm:text-xl font-bold" style={{ color: 'var(--text-secondary)' }}>vs</div>
                        )}
                      </div>
                      
                      <div className="text-center">
                        {match.awayTeam.logoUrl && (
                          <img 
                            src={getTeamLogoUrl(match.awayTeam.logoUrl)}
                            alt={`${match.awayTeam.name} logo`}
                            className="w-8 h-8 sm:w-10 sm:h-10 object-contain justify-center mx-auto mb-2"
                          />
                        )}
                        <div className={`font-semibold text-sm sm:text-base break-words ${accentClass}`} style={!scored ? { color: 'var(--text-primary)' } : undefined}>{match.awayTeam.name}</div>
                        {user && <TeamFormIndicator teamId={match.awayTeam.id} teamForms={teamForms} />}
                      </div>
                    </div>
                  </div>

                  <div className="text-center">
                    {predictionOpen && user ? (
                      // Always show prediction form for open matches when user is logged in
                      <div className="space-y-2">
                        <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 items-center mb-4">
                          <div className="text-center">
                            <ScoreInput
                              value={predictionForms[match.id]?.homeGoals || ''}
                              onChange={(value) => handlePredictionChange(match.id, 'homeGoals', value)}
                              min={0}
                              max={9}
                            />
                          </div>
                          
                          <div className="text-center text-2xl font-bold" style={{ color: 'var(--text-secondary)' }}>
                            -
                          </div>
                          
                          <div className="text-center">
                            <ScoreInput
                              value={predictionForms[match.id]?.awayGoals || ''}
                              onChange={(value) => handlePredictionChange(match.id, 'awayGoals', value)}
                              min={0}
                              max={9}
                            />
                          </div>
                        </div>
                        
                        {(!bankerMatchIdInWeek || bankerMatchIdInWeek === match.id) && (
                          <BankerButton
                            isActive={
                              predictionForms[match.id] !== undefined
                                ? Boolean(predictionForms[match.id]?.isBanker)
                                : Boolean(prediction?.isBanker)
                            }
                            onToggle={() => handleBankerToggle(match.id)}
                            tooltipText={
                              <>
                                Banker predictions earn double points if correct, but lose 1 point if incorrect.
                                <br />
                                They can be used once per round.
                              </>
                            }
                          />
                        )}
                        
                        <div className="flex items-center justify-center space-x-2">
                          {(() => {
                            // Get the saved prediction for this match
                            const saved = prediction;
                            const form = predictionForms[match.id];

                            // If no prediction yet, only show button when both scores are filled
                            if (!saved) {
                              const bothFilled = areBothScoresFilled(match.id);
                              const countdownValue = countdownValues[match.id];
                              
                              if (!bothFilled) {
                                return null; // Don't show button until both scores are filled
                              }
                              
                              return (
                                <div className="flex flex-col items-center space-y-2">

                                  <button
                                    onClick={() => {
                                      cancelAutoSubmit(match.id);
                                      handlePredictionSubmit(match.id);
                                    }}
                                    disabled={submitting[match.id]}
                                    className="px-4 py-2 text-sm rounded btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {submitting[match.id] ? 'Saving...' : 'Submit Prediction'}
                                  </button>

                                  {hasActiveCountdown(match.id) && countdownValue && (
                                    <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                      Auto-submitting in {countdownValue} second{countdownValue !== 1 ? 's' : ''}...
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            // If prediction exists, only show button if the form has changed
                            const changed =
                              form &&
                              (form.homeGoals !== saved.homeGoals.toString() ||
                                form.awayGoals !== saved.awayGoals.toString() ||
                                form.isBanker !== saved.isBanker);

                            if (changed) {
                              return (
                                <div className="flex mt-4 flex-row items-center justify-center space-x-2">
                                  <button
                                    onClick={() => {
                                      const currentPrediction = getPredictionForMatch(match.id);
                                      if (currentPrediction) {
                                        setPredictionForms(prev => ({
                                          ...prev,
                                          [match.id]: {
                                            homeGoals: currentPrediction.homeGoals.toString(),
                                            awayGoals: currentPrediction.awayGoals.toString(),
                                            isBanker: currentPrediction.isBanker
                                          }
                                        }));
                                      } else {
                                        setPredictionForms(prev => ({
                                          ...prev,
                                          [match.id]: {
                                            homeGoals: '',
                                            awayGoals: '',
                                            isBanker: false
                                          }
                                        }));
                                      }
                                    }}
                                    className="px-4 py-2 text-sm rounded btn-secondary"
                                  >
                                    Reset
                                  </button>

                                  <button
                                    onClick={() => handlePredictionSubmit(match.id)}
                                    disabled={submitting[match.id] || !form?.homeGoals || !form?.awayGoals}
                                    className="px-4 py-2 text-sm rounded btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {submitting[match.id] ? 'Saving...' : 'Update Prediction'}
                                  </button>
                                </div>
                              );
                            }

                            // If not changed, show nothing
                            return (
                              <div className="py-4">
                              </div>
                            )
                          })()}
                        </div>
                      </div>
                    ) : predictionOpen && !user ? (
                      // Show sign-up call-to-action for non-logged-in users when predictions are open
                      <div className="space-y-2">
                        {match.homeGoals !== undefined && match.homeGoals !== null && 
                         match.awayGoals !== undefined && match.awayGoals !== null ? (
                          <div>
                            <div className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                              {match.homeGoals} - {match.awayGoals}
                            </div>
                            <div className="text-sm font-semibold mb-2" style={{ color: '#22c55e' }}>Final Result</div>
                          </div>
                        ) : (
                          <></>
                        )}
                        <Link
                          to="/register"
                          className="inline-block px-4 py-2 text-sm font-medium rounded-md btn-primary hover:opacity-90 transition-opacity"
                        >
                          Sign up & enter your prediction
                        </Link>
                        <div className="text-xs mt-2 mb-2 pt-4">
                          <Link to="/login" style={{ color: 'var(--highlight)' }}>
                            Already have an account? Log in
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {scored && prediction && (
                          <ScoredMatchSummary
                            prediction={prediction}
                            outcome={outcome as 'exact' | 'correct' | 'wrong'}
                          />
                        )}

                        {outcome === 'missed' && <MissedMatchSummary />}

                        {outcome === 'predicted' && prediction && !predictionOpen && (
                          <PendingPredictionSummary prediction={prediction} />
                        )}
                      </div>
                    )}
                  </div>

                  {!predictionOpen && match.status !== 'finished' && !(prediction && outcome === 'predicted') && (
                    <div className="text-sm text-center mt-2" style={{ color: 'var(--text-secondary)' }}>
                      Predictions closed
                    </div>
                  )}

                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default SeasonDetail;